import { useEffect, useRef } from 'react'
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useSearchParams,
  useParams,
  useLocation,
} from 'react-router-dom'
import { Toaster } from 'sonner'
import { observer } from 'mobx-react-lite'
import HomePage from '@/pages/HomePage'
import PaymentProcessingPage from '@/pages/PaymentProcessingPage'
import { DepositPage } from '@/pages/DepositPage'
import { PaymentConfirmationPage } from '@/pages/PaymentConfirmationPage'
import WithdrawPageNew from '@/pages/WithdrawPageNew'
import CouponPage from '@/pages/CouponPage'
import RechargeCouponPage from '@/pages/RechargeCouponPage'
import LotteryPage from '@/pages/LotteryPage'
import LoginPage from '@/pages/LoginPage'
import RegisterPage from '@/pages/RegisterPage'
import ParrainagePage from '@/pages/ParrainagePage'
import { AffiliateWithdrawPage } from '@/pages/AffiliateWithdrawPage'
import ScratchCardPage from '@/pages/ScratchCardPage'
import TransactionsPage from '@/pages/TransactionsPage'
import AccountPage from '@/pages/AccountPage'
import JeuxPage from '@/pages/JeuxPage'
import InstallPage from '@/pages/InstallPage'
import ProtectedRoute from '@/components/ProtectedRoute'
import PublicOnlyRoute from '@/components/PublicOnlyRoute'
import { InAppBrowserBlocker } from '@/components/InAppBrowserBlocker'
import { isMobileApp } from '@/utils/browser'
import { getCommercialIdFromUrl } from '@/utils/partner'
import { InfoBanner, InfoBannerService, useAutoShowBanner } from '@/components/InfoBanner'
import { analyticsService } from '@/services/analytics'
import { errorTracker } from '@/services/errorTracking'
import { initStorage } from '@/lib/storage'
import { useCoreController } from '@/services/coreController'
import { useAuth, AuthProvider } from '@/hooks/useAuth'
import { usePhoneVerification } from '@/hooks/usePhoneVerification'
import { PhoneVerificationBanner } from '@/components/PhoneVerificationBanner'
import { CaptureCommercialIdRedirect } from '@/components/CaptureCommercialIdRedirect'
import { ThemeProvider } from '@/hooks/useTheme'

const AppContent = observer(function AppContent() {
  const [searchParams] = useSearchParams()
  const { commercialId } = useParams()
  const coreController = useCoreController()
  const { banner, showBanner } = useAutoShowBanner()
  const bannerProcessedRef = useRef(false)
  const settingsLoadedRef = useRef(false)
  const { showPrompt, dismissPrompt, markVerified } = usePhoneVerification()

  useEffect(() => {
    if (settingsLoadedRef.current) return
    coreController.loadApplicationSetting()
    settingsLoadedRef.current = true
  }, [coreController])

  useEffect(() => {
    if (bannerProcessedRef.current) return
    if (!coreController.loading && coreController.appSettingResponse?.banner) {
      const bannerData = coreController.appSettingResponse.banner
      const commercialId = coreController.getCommercialId() || ''
      InfoBannerService.showBannerIfActive(bannerData, commercialId, showBanner)
      bannerProcessedRef.current = true
    }
  }, [coreController.loading, showBanner])

  const location = useLocation()

  useEffect(() => {
    // Bug #3 fix: this effect must NOT re-fire on every route navigation.
    // Previously, getCommercialIdFromPath() would return the current route
    // name (e.g. "payment") and clobber the stored commercial_id. Now we
    // only listen to explicit inputs (URL params / query), not to pathname.
    //
    // Path-based capture is handled by CaptureCommercialIdRedirect for the
    // dedicated /:commercialId and /share/:commercialId routes, which is the
    // ONLY path where a path segment should ever be treated as a commercial id.
    const commercialIdFromQuery = searchParams.get('commercialId')
    const incoming = getCommercialIdFromUrl() || commercialIdFromQuery || commercialId || null
    if (!incoming) return

    const current = coreController.getCommercialId()
    if (current !== incoming) {
      coreController.setCommercialId(incoming)
      analyticsService.setCommercialId(incoming)
    }
    // location is referenced only to keep React Router aware of the active
    // route, but it is intentionally NOT in the dependency array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, commercialId, coreController])

  return (
    <>
      <Routes>
        {/* PUBLIC AUTH — own stack, must not be visible to logged-in users */}
        <Route
          path="/login"
          element={
            <PublicOnlyRoute>
              <LoginPage />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicOnlyRoute>
              <RegisterPage />
            </PublicOnlyRoute>
          }
        />

        {/* ROOT TABS — bottom of the stack */}
        <Route path="/" element={<HomePage />} />
        <Route path="/jeux" element={<JeuxPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/transactions" element={<TransactionsPage />} />

        {/* DETAIL / DEEP ROUTES — guarded, stack on top of a tab */}
        <Route
          path="/lottery"
          element={
            <ProtectedRoute>
              <LotteryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/deposit"
          element={
            <ProtectedRoute>
              <DepositPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment"
          element={
            <ProtectedRoute>
              <PaymentProcessingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment-confirmation"
          element={
            <ProtectedRoute>
              <PaymentConfirmationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/withdraw"
          element={
            <ProtectedRoute>
              <WithdrawPageNew />
            </ProtectedRoute>
          }
        />
        <Route
          path="/coupons"
          element={
            <ProtectedRoute>
              <CouponPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/recharge-coupon"
          element={
            <ProtectedRoute>
              <RechargeCouponPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/scratch-card"
          element={
            <ProtectedRoute>
              <ScratchCardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/parrainage"
          element={
            <ProtectedRoute>
              <ParrainagePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/affiliate-withdraw"
          element={
            <ProtectedRoute>
              <AffiliateWithdrawPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/install"
          element={
            isMobileApp() ? <Navigate to="/" replace /> : <InstallPage />
          }
        />

        {/* REDIRECTS — always replace, never stack */}
        <Route
          path="/dashboard"
          element={<Navigate to="/parrainage" replace />}
        />
        <Route
          path="/share/:commercialId"
          element={<CaptureCommercialIdRedirect />}
        />
        <Route
          path="/:commercialId"
          element={<CaptureCommercialIdRedirect />}
        />

        {/* CATCH-ALL */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <Toaster position="top-right" richColors />
      <InAppBrowserBlocker />

      <PhoneVerificationBanner
        open={showPrompt}
        onDismiss={dismissPrompt}
        onVerified={(phone) => markVerified(phone, true)}
      />

      {banner && (
        <InfoBanner
          banner={banner}
          commercialId={coreController.getCommercialId() || ''}
        />
      )}
    </>
  )
})

function App() {
  useEffect(() => {
    initStorage().then(() => console.debug('✅ Storage initialized'))
    analyticsService.initialize()
    errorTracker.initialize()
  }, [])

  return (
    <ThemeProvider>
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  )
}

export default App